/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    ButtonGroup,
    Button,
    Icon,
    Text,
    Heading,
    Stack,
    Box,
    Inline,
    RadioGroupWithValidation,
    Radio,
    FlexRow,
    FlexCol,
    useBreakpoint,
    useFlags,
    FocusOnMount,
} from 'sarsaparilla';
import PassTypesTable from './PassTypesTable';
import { PassTypeProps } from '../props';
import RenderIf from '../utils/JSXUtils';
import { showSitePassAlert as showSitePassAlertUtil } from '../utils/launchDarklyUtils';

function HelperWizard({
    facilityId = '',
    parentName,
    facilityAgency = '',
    initialStep = 0,
    selectedPassTypeId,
    passTypes,
    passTypesRaw,
    pricing,
    passTypesOptions = [],
    status,
    showDescription = true,
    isInWidget = false,
    onSelectPass,
    onRemovePass,
    onReviewOptionsClick = () => {},
    onViewAllPassesClick = () => {},
    onSaveReservation = () => {},
    renderInfoAlert = () => {},
}) {
    const [helperWizardState, setHelperWizardState] = useState({ step: 0, passType: '' });
    const passTypeInputRef = useRef();
    const breakpoint = useBreakpoint();
    const { facilitiesUsingSitePassIntegration } = useFlags();
    const viewAllSitePassesText = 'View All Site Passes';
    const HelpMeChooseSitePassText = 'Help Me Choose a Site Pass';
    const isNPSagency = facilityAgency === 'NPS';

    //Filter to only return non-commercial passes
    const filteredStandardRawPassTypes = useMemo(
        () => passTypesRaw.filter((item) => !item?.features?.['is-commercial-pass']),
        [passTypesRaw]
    );

    // launch darkly flags does not work for the widget
    const shouldShowSitePassAlert = useMemo(() => {
        return (
            showSitePassAlertUtil(facilityId, facilitiesUsingSitePassIntegration) ||
            isNPSagency ||
            isInWidget
        );
    }, [facilitiesUsingSitePassIntegration, facilityId, isInWidget, isNPSagency]);

    const filteredPassTypes = useMemo(() => {
        const selectedPassType = helperWizardState?.passType;
        if (!selectedPassType) {
            return passTypes;
        }

        const newPassTypes = passTypes.filter((item) => {
            // don't show passes with transportation type equals to 'all' when individual pass selected
            return (
                (item?.transportation_type === selectedPassType ||
                    (item?.transportation_type === 'all' &&
                        selectedPassType !== 'individual')) &&
                !item?.features?.['is-commercial-pass']
            );
        });

        return newPassTypes;
    }, [helperWizardState?.passType, passTypes]);

    const isLoading = useMemo(() => {
        return status === 'LOADING';
    }, [status]);

    useEffect(() => {
        if (initialStep >= 0) {
            setHelperWizardState((prevState) => ({ ...prevState, step: initialStep }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePassTypeChange = (e) => {
        const radioPassTypeValue = e.target.value;
        setHelperWizardState((prevState) => ({
            ...prevState,
            passType: radioPassTypeValue,
        }));
    };

    const handlePreviousStep = () => {
        const prevStep = (helperWizardState?.step || 0) - 1;
        const step = prevStep >= 0 ? prevStep : 0;
        setHelperWizardState((prevState) => ({ ...prevState, step }));
    };

    const handleStartOver = () => {
        setHelperWizardState((prevState) => ({ ...prevState, step: 0, passType: '' }));
    };

    const handleNextStep =
        (resetWizard = false) =>
        async () => {
            if (resetWizard) {
                const saved = await onSaveReservation();
                if (!saved) {
                    return;
                }
            }
            const minValue = resetWizard ? 0 : 2;
            const nextStep = Math.min((helperWizardState?.step || 0) + 1, minValue);
            setHelperWizardState((prevState) => ({ ...prevState, step: nextStep }));
        };

    const validatePassType = () => {
        const validation = passTypeInputRef.current.validate();

        if (!validation?.isInvalid) {
            handleNextStep()();
        }
    };

    const renderDefaultStep = () => {
        return (
            <>
                {renderInfoAlert()}
                <Heading appearance="h4" headingLevel={2}>
                    An entrance pass is required at this location
                </Heading>
                <Box>
                    <Stack space="xs">
                        <Text>A digital Site Pass:</Text>
                        <ul className="site-pass-helper-wizard__list">
                            <li className="site-pass-helper-wizard__list-item">
                                <Inline space="xs">
                                    <Icon
                                        iconName="check-circle"
                                        aria-hidden
                                        className="site-pass-helper-wizard__list-item-icon"
                                        size="sm"
                                    />
                                    <Text>
                                        {`Covers the entrance fee for ${parentName}`}
                                    </Text>
                                </Inline>
                            </li>
                            <li className="site-pass-helper-wizard__list-item">
                                <Inline space="xs">
                                    <Icon
                                        iconName="check-circle"
                                        aria-hidden
                                        className="site-pass-helper-wizard__list-item-icon"
                                        size="sm"
                                    />
                                    <Text>
                                        Is available for download on a phone or tablet
                                    </Text>
                                </Inline>
                            </li>
                        </ul>
                        {RenderIf(shouldShowSitePassAlert)(
                            <Inline space="xxs">
                                {isInWidget && (
                                    <Text fontWeight="semibold">
                                        Site Passes are non-refundable. Before purchasing,
                                        review additional options you might be eligible
                                        for below.
                                    </Text>
                                )}
                                {!isInWidget && (
                                    <>
                                        <Text fontWeight="semibold">
                                            Site Passes are non-refundable. Before
                                            purchasing,
                                        </Text>
                                        <Button
                                            appearance="link"
                                            onClick={onReviewOptionsClick}
                                            gaTrackingId="488535759505"
                                        >
                                            review these additional options
                                        </Button>
                                        <Text fontWeight="semibold">
                                            you might be eligible for.
                                        </Text>
                                    </>
                                )}
                            </Inline>
                        )}
                    </Stack>
                </Box>
                {breakpoint?.isMdAndUp && (
                    <ButtonGroup align="right">
                        <Button
                            appearance="tertiary"
                            isDisabled={isLoading}
                            onClick={onViewAllPassesClick}
                            gaTrackingId="244418425849"
                        >
                            {viewAllSitePassesText}
                        </Button>
                        {RenderIf(passTypesOptions?.length)(
                            <Button
                                isDisabled={isLoading}
                                onClick={handleNextStep()}
                                gaTrackingId="870820134122"
                            >
                                {HelpMeChooseSitePassText}
                            </Button>
                        )}
                    </ButtonGroup>
                )}
                {breakpoint?.isSmAndDown && (
                    <ButtonGroup align="right">
                        {RenderIf(passTypesOptions?.length)(
                            <Button
                                isDisabled={isLoading}
                                onClick={handleNextStep()}
                                gaTrackingId="412643661022"
                            >
                                {HelpMeChooseSitePassText}
                            </Button>
                        )}
                        <Button
                            appearance="tertiary"
                            isDisabled={isLoading}
                            onClick={onViewAllPassesClick}
                            gaTrackingId="268831340281"
                        >
                            {viewAllSitePassesText}
                        </Button>
                    </ButtonGroup>
                )}
            </>
        );
    };

    const renderFirstStep = () => {
        return (
            <>
                {renderInfoAlert()}
                <FocusOnMount>
                    <Heading appearance="h4" headingLevel={2}>
                        Which pass do I need?
                    </Heading>
                </FocusOnMount>
                <Box>
                    <Stack space="xs">
                        <Text>
                            Answer this question to get your recommended pass or view all
                            your options by clicking &quot;{viewAllSitePassesText}
                            &quot;.
                        </Text>
                        <RadioGroupWithValidation
                            id="pass-type"
                            name="pass-type"
                            ref={passTypeInputRef}
                            label="What transportation will you use to enter the park?"
                            isLabelVisible
                            isRequired
                            value={helperWizardState?.passType}
                            onChange={handlePassTypeChange}
                        >
                            {passTypesOptions?.map((passTypesOption) => (
                                <Radio
                                    key={passTypesOption?.transportationType}
                                    id={passTypesOption?.transportationType}
                                    label={passTypesOption?.transportationTypeName}
                                    value={passTypesOption?.transportationType}
                                />
                            ))}
                        </RadioGroupWithValidation>
                    </Stack>
                </Box>
                <FlexRow alignItems="center" justifyContent="space-between">
                    <FlexCol md="variable" xsOrder="last" mdOrder="first">
                        <ButtonGroup className="mt-2 mt-md-0" align="left">
                            <Button
                                appearance="link"
                                isDisabled={isLoading}
                                onClick={onViewAllPassesClick}
                                gaTrackingId="610536664070"
                            >
                                {viewAllSitePassesText}
                            </Button>
                        </ButtonGroup>
                    </FlexCol>
                    <FlexCol md="variable" xsOrder="first" mdOrder="last">
                        {breakpoint?.isMdAndUp && (
                            <ButtonGroup align="right">
                                <Button
                                    appearance="tertiary"
                                    isDisabled={isLoading}
                                    onClick={handlePreviousStep}
                                    gaTrackingId="098583808998"
                                >
                                    Back
                                </Button>
                                <Button
                                    isLoading={isLoading}
                                    onClick={validatePassType}
                                    gaTrackingId="726837309223"
                                >
                                    Continue
                                </Button>
                            </ButtonGroup>
                        )}
                        {/* mobile view show back button on bottom */}
                        {breakpoint?.isSmAndDown && (
                            <ButtonGroup align="right">
                                <Button
                                    isLoading={isLoading}
                                    onClick={validatePassType}
                                    gaTrackingId="357036109898"
                                >
                                    Continue
                                </Button>
                                <Button
                                    appearance="tertiary"
                                    isDisabled={isLoading}
                                    onClick={handlePreviousStep}
                                    gaTrackingId="437389057005"
                                >
                                    Back
                                </Button>
                            </ButtonGroup>
                        )}
                    </FlexCol>
                </FlexRow>
            </>
        );
    };

    const renderSecondStep = () => {
        return (
            <>
                {renderInfoAlert()}
                <FocusOnMount>
                    <Heading appearance="h4" headingLevel={2}>
                        Based on your selection, we recommend choosing one of the passes
                        below
                    </Heading>
                </FocusOnMount>
                <Box>
                    <PassTypesTable
                        selectedPassTypeId={selectedPassTypeId}
                        passTypes={filteredPassTypes}
                        passTypesRaw={filteredStandardRawPassTypes}
                        status={status}
                        showDescription={showDescription}
                        pricing={pricing}
                        onSelectPass={onSelectPass}
                        onRemovePass={onRemovePass}
                    />
                </Box>
                <FlexRow alignItems="center" justifyContent="space-between">
                    <FlexCol md="variable" xsOrder="last" mdOrder="first">
                        <ButtonGroup align="left" className="mt-2 mt-md-0">
                            <Button
                                appearance="link"
                                isDisabled={isLoading}
                                onClick={onViewAllPassesClick}
                                gaTrackingId="738900336498"
                            >
                                {viewAllSitePassesText}
                            </Button>
                        </ButtonGroup>
                    </FlexCol>
                    <FlexCol md="variable" xsOrder="first" mdOrder="last">
                        {/* mobile view - show back button on bottom */}
                        {(() => {
                            const backButton = (
                                <Button
                                    appearance="tertiary"
                                    isDisabled={isLoading}
                                    onClick={handlePreviousStep}
                                >
                                    Back
                                </Button>
                            );

                            const startOverButton = (
                                <Button
                                    appearance="primary"
                                    isDisabled={isLoading}
                                    onClick={handleStartOver}
                                >
                                    Start Over
                                </Button>
                            );

                            return (
                                <ButtonGroup align="right">
                                    {breakpoint?.isMdAndUp ? (
                                        <>
                                            {backButton}
                                            {startOverButton}
                                        </>
                                    ) : (
                                        <>
                                            {startOverButton}
                                            {backButton}
                                        </>
                                    )}
                                </ButtonGroup>
                            );
                        })()}
                    </FlexCol>
                </FlexRow>
            </>
        );
    };

    const renderSelectedStep = () => {
        switch (helperWizardState?.step) {
            case 1:
                return renderFirstStep();

            case 2:
                return renderSecondStep();

            default:
                return renderDefaultStep();
        }
    };

    return <Stack className="site-pass-helper-wizard">{renderSelectedStep()}</Stack>;
}

HelperWizard.propTypes = {
    facilityId: PropTypes.string,
    facilityAgency: PropTypes.string,
    initialStep: PropTypes.number,
    selectedPassTypeId: PropTypes.string,
    passTypes: PropTypes.arrayOf(PassTypeProps).isRequired,
    passTypesRaw: PropTypes.arrayOf(PassTypeProps).isRequired,
    pricing: PropTypes.node,
    passTypesOptions: PropTypes.array,
    status: PropTypes.string,
    showDescription: PropTypes.bool,
    isInWidget: PropTypes.bool,
    onSelectPass: PropTypes.func.isRequired,
    onRemovePass: PropTypes.func.isRequired,
    onReviewOptionsClick: PropTypes.func,
    onViewAllPassesClick: PropTypes.func,
    onSaveReservation: PropTypes.func,
    renderInfoAlert: PropTypes.func,
};

export default HelperWizard;
